/* Bearer JWT auth client.
 *
 * Per ADR-028:
 *   - Single issuer at auth.siliconproof.io
 *   - access_token (15 min) + refresh_token (30 days, rotated)
 *   - Tokens go in `Authorization: Bearer <access>` header — NO cookies
 *   - Storage = sessionStorage, never localStorage (per R1 XSS surface)
 *
 * Per saas-greenfield-pitfalls §3 / §10:
 *   - Pure functions for token I/O so they have unit tests
 *   - Never silent-fallback on auth failure — surface errors to the caller
 *
 * The Zustand store gives components a reactive `isAuthenticated` /
 * `user` signal without forcing them to subscribe to storage events. */

import { create } from 'zustand';

const ACCESS_KEY = 'sp:aq:access';
const REFRESH_KEY = 'sp:aq:refresh';
const USER_KEY = 'sp:aq:user';

export interface UserClaims {
  /** User UUID (BetterAuth `user.id`). */
  sub: string;
  /** Tenant UUID this user belongs to (the Customer tier per ADR-018). */
  tenant_id: string;
  /** OEM UUID — drives white-label branding. */
  oem_id: string;
  /** Vertical scope of this token (sanity check: must equal 'aq'). */
  vertical: string;
  /** Cell ID — usually 'cell-default'. */
  cell_id: string;
  /** Role within the tenant: owner | admin | operator | viewer. */
  role: string;
  /** Granular permissions (scope strings). */
  scopes?: string[];
  /** Display fields cached from /me — not authoritative. */
  email?: string;
  name?: string;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
}

export interface SignInResult {
  ok: true;
  user: UserClaims;
  tokens: AuthTokens;
}
export interface SignInError {
  ok: false;
  error: string;
  code?: string;
}

interface AuthState {
  user: UserClaims | null;
  isAuthenticated: boolean;
  setSession: (user: UserClaims, tokens: AuthTokens) => void;
  clearSession: () => void;
  hydrate: () => void;
}

// ---------- Pure functions (testable) ----------

export function readAccessToken(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(ACCESS_KEY);
}

export function readRefreshToken(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(REFRESH_KEY);
}

function writeTokens(tokens: AuthTokens): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(ACCESS_KEY, tokens.access_token);
  sessionStorage.setItem(REFRESH_KEY, tokens.refresh_token);
}

function writeUser(user: UserClaims): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
}

function readUser(): UserClaims | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserClaims;
  } catch {
    return null;
  }
}

function clearAllStorage(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(ACCESS_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(USER_KEY);
}

/** Classify an auth error string into network vs auth vs server.
 *  Exported as a pure function for unit testing (per pitfalls §10). */
export function classifyAuthError(msg: string | undefined | null): {
  isNetwork: boolean;
  isAuth: boolean;
  isServer: boolean;
} {
  if (!msg) return { isNetwork: false, isAuth: false, isServer: false };
  return {
    isNetwork: /load failed|network|fetch|gateway|503|502|504/i.test(msg),
    isAuth: /unauthorized|forbidden|invalid.credentials|invalid.email|401|403/i.test(msg),
    isServer: /internal|500/i.test(msg),
  };
}

// ---------- HTTP calls (auth backend) ----------

function authBaseUrl(): string {
  // Vite injects `import.meta.env`; fall back to relative '/auth' in dev (proxy)
  const explicit = import.meta.env.VITE_AUTH_URL as string | undefined;
  if (explicit) return explicit;
  if (import.meta.env.DEV) return '/auth';
  return 'https://auth.siliconproof.io/api/auth';
}

async function postJson<T>(path: string, body: unknown, token?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${authBaseUrl()}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const j = (await res.json()) as { error?: string; message?: string };
      detail = j.error || j.message || detail;
    } catch {
      detail = `${res.status} ${res.statusText}`;
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

export async function signIn(email: string, password: string): Promise<SignInResult | SignInError> {
  try {
    const data = await postJson<{ access_token: string; refresh_token: string; user: UserClaims }>(
      '/sign-in/email',
      { email, password },
    );
    return { ok: true, user: data.user, tokens: { access_token: data.access_token, refresh_token: data.refresh_token } };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'sign-in failed';
    return { ok: false, error: msg };
  }
}

export async function signOut(): Promise<void> {
  const refresh = readRefreshToken();
  if (!refresh) return;
  try {
    await postJson('/sign-out', { refresh_token: refresh });
  } catch {
    // Always wipe local session — even if remote revocation fails the
    // local tokens are no longer usable as an auth credential against
    // sp-api (15-min expiry).
  }
}

/** Exchange a refresh token for a fresh access token.
 *  Returns the new tokens; the server may rotate the refresh token. */
export async function refreshAccessToken(): Promise<AuthTokens | null> {
  const refresh = readRefreshToken();
  if (!refresh) return null;
  try {
    const data = await postJson<{ access_token: string; refresh_token: string }>(
      '/refresh',
      { refresh_token: refresh },
    );
    return { access_token: data.access_token, refresh_token: data.refresh_token };
  } catch {
    return null;
  }
}

// ---------- Zustand store ----------

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  setSession: (user, tokens) => {
    writeTokens(tokens);
    writeUser(user);
    set({ user, isAuthenticated: true });
  },
  clearSession: () => {
    clearAllStorage();
    set({ user: null, isAuthenticated: false });
  },
  hydrate: () => {
    const access = readAccessToken();
    const user = readUser();
    if (access && user) set({ user, isAuthenticated: true });
  },
}));

/** Convenience hook — returns the same shape used by KAIOT components. */
export function useAuth(): {
  user: UserClaims | null;
  isAuthenticated: boolean;
  signIn: typeof signIn;
  signOut: () => Promise<void>;
} {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return {
    user,
    isAuthenticated,
    signIn,
    signOut: async () => {
      await signOut();
      useAuthStore.getState().clearSession();
    },
  };
}

/** Token-rotation listener — refresh access token whenever the tab regains
 *  focus AND >10 minutes since last refresh. Mount once at app boot. */
export function installVisibilityRefresh(): () => void {
  let lastRefresh = Date.now();
  const REFRESH_INTERVAL_MS = 10 * 60 * 1000;
  const onVisible = async (): Promise<void> => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastRefresh < REFRESH_INTERVAL_MS) return;
    if (!readRefreshToken()) return;
    const tokens = await refreshAccessToken();
    if (tokens) {
      writeTokens(tokens);
      lastRefresh = Date.now();
    } else {
      // Refresh failed — clear session so RouteGuard kicks the user to /login
      useAuthStore.getState().clearSession();
    }
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => document.removeEventListener('visibilitychange', onVisible);
}
